import { useEffect, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from 'redux/toolkit/hooks'
import { getErrorMessage, isFailed, isPending, isSuccess } from 'redux/toolkit/api'
import { MessageSource } from 'types/Messages'
import { getMessageSource } from 'redux/features/mstore/mstoreSlice'
import { useMessageDetailsContentLogic } from 'components/libs/message/messageDetails/messageDetailsContent/messageDetailsContentTabs/useMessageDetailsContentLogic'
import useUserRights, { UserRights } from 'components/libs/userRights/useUserRights'

export interface State {
  isGetMessageSourcePending: boolean
  isGetMessageSourceSuccess: boolean
  isGetMessageSourceFailed: boolean
  getMessageSourceError: string | undefined
  isShowSourceDisabled: boolean | undefined
  showSourceDisabledReason: string | undefined
  messageSource: MessageSource | undefined
}

export type MessageDetailsContentTabMessageSourceLogic = [State]

export const useMessageDetailsContentTabMessageSourceLogic = (): MessageDetailsContentTabMessageSourceLogic => {
  const [{ hasContent, cantViewQuarantined, cantViewBlocked, isLargeMessage, isAtpAffected }] =
    useMessageDetailsContentLogic()
  const dispatch = useAppDispatch()
  const {
    message,
    isGetMessageSourceSuccess,
    isGetMessageSourceFailed,
    getMessageSourceError,
    isGetMessageSourcePending,
    messageSource,
    showImages,
    userId
  } = useAppSelector(_store => ({
    message: _store.mstore.message,
    isGetMessageSourcePending: isPending(_store.mstore.getMessageSourceApiStatus),
    isGetMessageSourceSuccess: isSuccess(_store.mstore.getMessageSourceApiStatus),
    isGetMessageSourceFailed: isFailed(_store.mstore.getMessageSourceApiStatus),
    getMessageSourceError: getErrorMessage(_store.mstore.getMessageSourceApiStatus),
    messageSource: _store.mstore.messageSource,
    showImages: _store.mstore.showImages,
    userId: _store.auth.accessTokenObject?.userId
  }))
  const { userHasRight } = useUserRights()

  const isShowSourceDisabled = useMemo(
    () => isAtpAffected || cantViewBlocked || cantViewQuarantined || !hasContent,
    [cantViewBlocked, cantViewQuarantined, isAtpAffected, hasContent]
  )

  const showSourceDisabledReason = useMemo(() => {
    if (!message) {
      return undefined
    }
    switch (true) {
      case isAtpAffected: {
        return 'atd_detected'
      }
      case cantViewBlocked: {
        return 'cant_view_blocked'
      }
      case cantViewQuarantined: {
        return 'cant_view_quarantined'
      }
      case !hasContent: {
        return 'no_content'
      }
      default:
        return ''
    }
  }, [cantViewBlocked, cantViewQuarantined, isAtpAffected, message, hasContent])

  useEffect(() => {
    if (!message) {
      return
    }
    if (message && messageSource && message.mid === messageSource.mid) {
      return
    }
    dispatch(
      getMessageSource({
        messageId: message.mid,
        parse: 0,
        headersOnly: isLargeMessage ? 1 : 0,
        showImages: showImages ? 1 : 0,
        domainId: message.did,
        userId: userHasRight(UserRights.SKIP_TO_SEND_USER_ID_FOR_MESSAGE_ACTIONS) ? undefined : userId
      })
    )
  }, [dispatch, isLargeMessage, message, messageSource, showImages, userHasRight, userId])

  return useMemo(
    () => [
      {
        isGetMessageSourcePending,
        isGetMessageSourceSuccess,
        isGetMessageSourceFailed,
        getMessageSourceError,
        isShowSourceDisabled,
        showSourceDisabledReason,
        messageSource
      }
    ],
    [
      getMessageSourceError,
      isGetMessageSourceFailed,
      isGetMessageSourcePending,
      isGetMessageSourceSuccess,
      isShowSourceDisabled,
      messageSource,
      showSourceDisabledReason
    ]
  )
}
